import type { PulseChart } from '@shared/chart';
import type { ReplayInput, ReplayInputs, ReplayOutcome } from '@shared/replay';
import {
  buildEngineConfig,
  step,
  type BurstCompletePayload,
  type BurstTapPayload,
  type EngineEvent,
  type EngineInput,
  type HpChangedPayload,
} from '@shared/engine/pulse/core';
import { createInitialState, type EngineState } from '@shared/engine/pulse/state';
import { computeAccuracy } from '@shared/scoring';
import {
  milestoneIsStar,
  milestoneTier,
  type MilestoneKind,
} from '@shared/progression';
import { AudioEngine, clampPlaybackRate } from './audio/scheduler';
import { loadVolumePrefs } from './audio/volumePrefs';
import { GameRenderer } from './rendering/renderer';
import type {
  FlashSpec,
  FloatingText,
  ParticleEmission,
  ShakeSpec,
} from './rendering/render/pulseRenderTypes';
import {
  KeyboardInputSource,
  ReplayInputSource,
  type InputSource,
} from './input/inputSource';
import { RunTimeline } from './input/runTimeline';
import type { RunTimingReport } from '@shared/antiCheat/runTiming';
import {
  BURST_CHAMBER_COLOUR,
  CEILING_Y,
  FLOOR_Y,
  PLAYER_X,
  PLAYFIELD_HEIGHT,
  PLAYFIELD_WIDTH,
} from '@shared/gameTypes';
import { resetEngineStateForNextLoop } from '@shared/engine/pulse/state';
import { LoopRunState } from './loopManager';
import { tutorialHintBudget } from './songProgress';

export type SessionStatus =
  | 'idle'
  | 'starting'
  | 'lead_in'
  | 'running'
  | 'paused'
  | 'awaiting_next_loop'
  | 'completed'
  | 'failed';

/**
 * Fired by the session whenever a star/cup milestone is crossed.
 * `kind` identifies which beat it was; `pause` is true only for the
 * loop-1 finale (star_3) so the PlayPage can show its "INFINITE MODE"
 * overlay before the run drops into infinite repeats.
 */
export interface MilestoneInfo {
  kind: MilestoneKind;
  loopIndex: number;
  starsEarned: number;
  cupsEarned: number;
  pause: boolean;
}

export interface GameSessionOptions {
  chart: PulseChart;
  canvas: HTMLCanvasElement;
  audioOffsetMs: number;
  replayInputs?: EngineInput[];
  onStatusChange?: (status: SessionStatus) => void;
  onComplete?: (outcome: ReplayOutcome) => void;
  onEvent?: (event: EngineEvent) => void;
  /**
   * Star/cup milestone detector callback. Called once per crossing
   * (each kind fires at most once per run). The session dispatches
   * the in-engine FX for the milestone before invoking this callback.
   */
  onMilestone?: (info: MilestoneInfo) => void;
  testHook?: boolean;
  /**
   * When true, the session uses an extended lead-in (8 beats instead of 4)
   * and asks the renderer to show a TAP SPACE hint on the first un-hit pad.
   * Used for the very first run a player makes.
   */
  firstTime?: boolean;
  /**
   * Enable multi-loop progression. When true, the session extends
   * past loop 1 into infinite-mode repeats — each one stepping the
   * playback rate up by a constant (see `loopSpeedRamp` in
   * `@shared/progression`) — and only ends when the player dies.
   * Tutorial chart and other "teaching" surfaces opt out by passing
   * false; the run finishes normally on loop-1 completion in that case.
   */
  enableLoops?: boolean;
}

const COMBO_MILESTONES = [10, 25, 50, 100];
const LEAD_IN_BEATS = 4;
const FIRST_TIME_LEAD_IN_BEATS = 8;
/**
 * Wall-time floor for the count-in. As infinite-mode loops accelerate
 * the music, a fixed 4-beat lead-in shrinks linearly with speed — at
 * loop 6 (2.5×) on a 120 BPM chart it's already only 0.8s, which is
 * not enough time for the player to react to the first pad. We add
 * beats to keep the count-in at least this long; since playback speed
 * is uncapped, the count-in holds at this wall duration on every loop
 * (the tick blips just pack closer together as the speed climbs).
 */
const MIN_LEAD_IN_WALL_SECONDS = 1.0;

/**
 * Pick a lead-in beat count that takes at least
 * {@link MIN_LEAD_IN_WALL_SECONDS} of wall time at the loop's start
 * speed. Returns the input unchanged when the natural count-in is
 * already long enough; only kicks in once the speed ramp has
 * compressed the count-in too far.
 *
 * Important: the speed used here MUST match what the audio engine
 * actually plays at, so we route it through the same
 * {@link clampPlaybackRate} the engine applies to the source. The two
 * therefore agree on the effective rate, keeping the count-in at the
 * intended wall duration no matter how high infinite mode climbs.
 */
function effectiveLeadInBeats(baseBeats: number, bpm: number, startSpeed: number): number {
  const effectiveSpeed = clampPlaybackRate(startSpeed);
  const wallTime = (baseBeats * 60) / bpm / effectiveSpeed;
  if (wallTime >= MIN_LEAD_IN_WALL_SECONDS) return baseBeats;
  return Math.ceil((MIN_LEAD_IN_WALL_SECONDS * bpm * effectiveSpeed) / 60);
}

export class GameSession {
  private chart: PulseChart;
  private engineConfig: ReturnType<typeof buildEngineConfig>;
  private state: EngineState;
  private audio: AudioEngine;
  private renderer: GameRenderer;
  private inputSource: InputSource;
  private particleEmissions: ParticleEmission[] = [];
  private flashes: FlashSpec[] = [];
  private floatingTexts: FloatingText[] = [];
  private shake: ShakeSpec = { amount: 0, decay: 0 };
  private rafId: number | null = null;
  private lastBeat = 0;
  private status: SessionStatus = 'idle';
  private opts: GameSessionOptions;
  private outcome: ReplayOutcome | null = null;
  private allEvents: EngineEvent[] = [];
  /**
   * Per-loop captured taps — `loopCapturedInputs[K]` is loop K's
   * captured inputs. We push a fresh `[]` whenever the engine signals
   * `completed` and we're about to roll into the next loop. Because
   * each loop's audio clock resets, every entry's beats are bounded
   * to `[0, totalBeats]`.
   */
  private loopCapturedInputs: ReplayInput[][] = [[]];
  /**
   * Anti-cheat timing recorder. Captures `(beat, wallMs, monoMs)` +
   * `event.isTrusted` so the server can verify the run progressed in
   * real time (catching TAS slowdown + synthetic-input replay). Kept
   * separate from {@link loopCapturedInputs} so the determinism-critical
   * replay format stays untouched.
   */
  private timeline: RunTimeline;
  private flipAnim = 0;
  private lastBeatPulseBeat = -1;
  private currentBeatPulse = 0;
  private comboMilestonesHit = new Set<number>();
  /** Combo *before* the current pad_resolved event, so we can detect breaks. */
  private comboBeforeEvent = 0;
  /** Indices into chart.sections of 'drop' sections that already fired their flash. */
  private firedDropSections = new Set<number>();
  private lastTickAt = performance.now();
  /**
   * Multi-loop manager. Tracks loop index, fired milestones, and the
   * per-loop speed ramp. Always present even when
   * {@link GameSessionOptions.enableLoops} is false — in that case it
   * just stays at loop 0 forever and only the loop-1 milestones
   * (stars) ever fire.
   */
  private loopState: LoopRunState;
  private enableLoops: boolean;
  // Set true once destroy() has run. start() checks this after each await so
  // a destroyed-during-startup session can't kick off a RAF loop or schedule
  // audio against a closing AudioContext.
  private destroyed = false;
  // True when the session paused itself because the tab was backgrounded.
  // Used to auto-resume on the visibilitychange back to visible if the
  // player hasn't manually paused since.
  private autoPaused = false;
  /**
   * How many empty-tap toasts ("WAIT FOR THE PAD!") we've shown this
   * run. Capped so the teaching feedback fires for the first few
   * misfires once the empty-tap penalty unlocks (after the 3-hit
   * grace), then falls through to the standard "-4 HP" floating text.
   * Per-session by design — a returning player gets the reminder if
   * they slip back into mashing in a later run. Budget shrinks once the
   * player has cleared a song so it stops nagging veterans.
   */
  private emptyTapToastsShown = 0;
  private readonly maxTutorialHints = tutorialHintBudget();
  /**
   * Canvas size in CSS px, kept in sync via `ResizeObserver`. Reading
   * the live `getBoundingClientRect()` every frame would force a
   * layout flush — the cache pattern lets the draw loop read width /
   * height synchronously without triggering one. Seeded from the
   * canvas's initial rect; updated only when the viewport actually
   * resizes.
   */
  private cachedRect = { width: 0, height: 0 };
  private resizeObserver: ResizeObserver | null = null;

  constructor(opts: GameSessionOptions) {
    this.opts = opts;
    this.chart = opts.chart;
    this.enableLoops = opts.enableLoops ?? false;
    this.loopState = new LoopRunState(this.chart.totalBeats);
    this.engineConfig = buildEngineConfig(this.chart);
    this.state = createInitialState(this.chart);
    this.audio = new AudioEngine({ audioOffsetMs: opts.audioOffsetMs });
    const vols = loadVolumePrefs();
    this.audio.setMasterVolume(vols.master);
    this.audio.setMusicVolume(vols.music);
    this.audio.setSfxVolume(vols.sfx);
    this.renderer = new GameRenderer(opts.canvas);
    this.attachResizeObserver();

    if (opts.replayInputs) {
      this.inputSource = new ReplayInputSource(opts.replayInputs);
    } else {
      this.inputSource = new KeyboardInputSource(
        () => this.audio.getCurrentBeat(),
        () => this.status === 'lead_in' || this.status === 'running',
      );
    }

    this.timeline = new RunTimeline({
      getBeatAtNow: () => this.audio.getCurrentBeat(),
      isClockRunning: () => this.status === 'running',
    });
    this.timeline.attach();

    if (opts.testHook) {
      (window as unknown as { __beatHeavenTestHook?: GameSessionTestHook }).__beatHeavenTestHook = this.buildTestHook();
    }

    // Tab-visibility handler: backgrounded tabs throttle requestAnimationFrame
    // hard but the AudioContext clock keeps running. Without this, the music
    // races ahead of the engine and (a) the player misses the action, (b)
    // hits don't register because their idea of "now" is far behind. Pausing
    // on hidden lets us resume where we left off when the user comes back.
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private onVisibilityChange = (): void => {
    if (document.hidden) {
      if (this.status === 'running' || this.status === 'lead_in') {
        this.autoPaused = true;
        this.pause();
      }
    } else if (this.autoPaused && this.status === 'paused') {
      this.autoPaused = false;
      // Surface failures explicitly: if AudioContext.resume() rejects (e.g.
      // the browser revoked the autoplay grant after a long backgrounding),
      // a silent rejection would leave the player on the pause overlay with
      // no obvious cause. Logging makes the failure visible in the console
      // while the pause overlay stays up so the player can hit Resume to
      // retry from a fresh user gesture.
      this.resume().catch((err) => {
        console.warn('Auto-resume after tab return failed:', err);
      });
    }
  };

  async start(): Promise<void> {
    if (this.destroyed) return;
    this.setStatus('starting');
    const baseLeadIn = this.opts.firstTime ? FIRST_TIME_LEAD_IN_BEATS : LEAD_IN_BEATS;
    const ramp = this.loopState.getCurrentSpeedRamp();
    const leadIn = effectiveLeadInBeats(baseLeadIn, this.chart.bpm, ramp.startSpeed);
    await this.audio.start(this.chart, leadIn, {
      speedRamp: ramp,
    });
    if (this.destroyed) return;
    this.audio.scheduleLeadInTicks(leadIn);
    this.setStatus('lead_in');
    // If the tab went hidden during the await above, the visibility
    // handler missed its chance because status was still 'starting'.
    // Recheck now and auto-pause so background play doesn't drift.
    if (document.hidden) {
      this.autoPaused = true;
      this.pause();
      return;
    }
    this.tick();
  }

  /**
   * Begin the next loop. Called by PlayPage once the loop-1 finale
   * overlay (the "INFINITE MODE / SPEED UP" card) has dismissed, and
   * called inline from {@link handleLoopComplete} for every silent
   * infinite-mode transition after that. Resets chart-relative engine
   * state, advances the loop index, restarts audio at the new playback
   * rate, and re-enters the tick loop with a fresh lead-in.
   */
  async startNextLoop(): Promise<void> {
    if (this.destroyed) return;
    if (this.status !== 'awaiting_next_loop') return;
    this.loopState.advanceLoop();
    resetEngineStateForNextLoop(this.state, this.chart);
    this.engineConfig = buildEngineConfig(this.chart);
    this.lastBeat = 0;
    this.lastBeatPulseBeat = -1;
    this.firedDropSections.clear();
    this.comboMilestonesHit.clear();
    this.comboBeforeEvent = this.state.combo;
    // Open a fresh capture bucket for the new loop. Each loop's beats
    // are relative to its own start (the audio clock resets), so the
    // server's headless runner needs them as a separate array to
    // simulate the cumulative outcome correctly.
    this.loopCapturedInputs.push([]);
    this.timeline.startLoop(this.loopCapturedInputs.length - 1);

    this.setStatus('starting');
    const ramp = this.loopState.getCurrentSpeedRamp();
    const leadIn = effectiveLeadInBeats(LEAD_IN_BEATS, this.chart.bpm, ramp.startSpeed);
    await this.audio.restartForLoop(this.chart, leadIn, ramp);
    if (this.destroyed) return;
    this.audio.scheduleLeadInTicks(leadIn);
    this.setStatus('lead_in');
    if (document.hidden) {
      this.autoPaused = true;
      this.pause();
      return;
    }
    this.lastTickAt = performance.now();
    this.tick();
  }

  /**
   * Live song beat for the current frame, derived from the audio clock.
   * Used by the tutorial overlay to swap instruction text on beat-anchored
   * cues; safe to call any time the session is active.
   */
  getCurrentBeat(): number {
    return this.audio.getCurrentBeat();
  }

  pause(): void {
    if (this.status === 'running' || this.status === 'lead_in') {
      this.setStatus('paused');
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      // Suspend the AudioContext so the music actually stops. Pre-scheduled
      // notes are anchored to ctx.currentTime which freezes while suspended;
      // when we resume, the audio picks up exactly where it was.
      void this.audio.suspend().catch((err) => {
        console.warn('Failed to suspend audio on pause:', err);
      });
    }
  }

  /**
   * Public pause API that also clears the auto-pause flag so a tab-return
   * after this won't surprise-resume the player.
   */
  manualPause(): void {
    this.autoPaused = false;
    this.pause();
  }

  async resume(): Promise<void> {
    if (this.status === 'paused') {
      // Clear auto-pause first - a manual resume should *also* mean any
      // subsequent visibility flip starts fresh.
      this.autoPaused = false;
      await this.audio.resume();
      this.setStatus('running');
      this.tick();
    }
  }

  async destroy(): Promise<void> {
    this.destroyed = true;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.inputSource.destroy();
    this.timeline.detach();
    await this.audio.stop();
    delete (window as unknown as { __beatHeavenTestHook?: GameSessionTestHook })
      .__beatHeavenTestHook;
  }

  getOutcome(): ReplayOutcome | null {
    return this.outcome;
  }

  getStatus(): SessionStatus {
    return this.status;
  }

  getCapturedReplay(): ReplayInputs {
    // One entry per loop the player attempted, including the loop they
    // died in (or the only loop, for single-loop runs). Each loop's
    // beats are relative to its own start so the server can replay
    // each loop against the same chart deterministically.
    return {
      loops: this.loopCapturedInputs.map((inputs) => ({ inputs: inputs.slice() })),
      audioOffsetMs: this.opts.audioOffsetMs,
    };
  }

  /**
   * Anti-cheat timing stream for this run (or `null` if nothing was
   * captured — e.g. a replay-preview run). Submitted alongside the
   * replay so the server can verify real-time progression.
   */
  getTimingReport(): RunTimingReport | null {
    return this.timeline.toReport();
  }

  /** Stars + cups earned this run, for the results overlay and submit body. */
  getProgressionEarnedThisRun(): { stars: number; cups: number } {
    return {
      stars: this.loopState.countStars(),
      cups: this.loopState.countCups(),
    };
  }

  /** Current loop index (0 = first loop). Used by the PlayPage overlay. */
  getLoopIndex(): number {
    return this.loopState.getLoopIndex();
  }

  /**
   * How many full chart-lengths the player completed this run. Each
   * time the engine emits `completed` and {@link advanceLoop} runs
   * the count goes up; a player who dies during loop 5 will have
   * cleared 5 loops. Always 0 for runs that opt out of infinite-mode
   * loops (replay verifier, tutorial chart) — those still hit the
   * engine's `state.completed` branch via {@link finishSession} and
   * surface as "Cleared!" via {@link ReplayOutcome.completed}, so the
   * cleared-count is only meaningful for the infinite-mode framing.
   */
  getLoopsCleared(): number {
    return this.loopState.getLoopIndex();
  }

  /**
   * Apply the latest persisted volume prefs to the running session. Called
   * whenever the in-game volume slider moves so the live audio reacts
   * instantly without waiting for the next session start.
   */
  applyVolumePrefs(): void {
    const vols = loadVolumePrefs();
    this.audio.setMasterVolume(vols.master);
    this.audio.setMusicVolume(vols.music);
    this.audio.setSfxVolume(vols.sfx);
  }


  private setStatus(s: SessionStatus): void {
    if (this.status === s) return;
    this.status = s;
    this.opts.onStatusChange?.(s);
  }

  private tick = (): void => {
    if (this.status === 'paused') return;

    const currentBeat = this.audio.getCurrentBeat();

    const now = performance.now();
    const dt = Math.min(0.05, Math.max(0, (now - this.lastTickAt) / 1000));
    this.lastTickAt = now;

    if (currentBeat < 0) {
      const leadInBeat = this.computeLeadInBeat();
      // Pass the (negative) beat through so the camera scrolls naturally
      // during the count-in and the first pad is visible *before* the player
      // has to react. Otherwise we'd freeze the camera at beat 0 and the
      // first pad would only fly in from the right edge once the music
      // started.
      this.drawFrame(currentBeat, leadInBeat);
      this.rafId = requestAnimationFrame(this.tick);
      return;
    }

    if (this.status === 'lead_in') {
      this.setStatus('running');
    }

    this.tickBeatPulse(currentBeat, dt);

    const cappedTarget = Math.min(currentBeat, this.lastBeat + this.audio.maxAdvanceBeatsPerFrame());

    const rawInputs = this.inputSource.poll(cappedTarget);
    // Drop inputs queued during lead-in (negative-beat). The InputSource
    // can't see lead-in state, so a player tapping along with the
    // countdown would otherwise have flipDown events clamped to beat 0
    // and processed as empty taps the moment the song starts (each
    // chunking HP via HP_EMPTY_TAP). Dropping them is correct: there are
    // no pads to hit during lead-in.
    const playableInputs = rawInputs.filter((inp) => inp.beat >= 0);
    const clampedInputs = playableInputs.map((inp) => ({
      beat: Math.max(this.lastBeat, Math.min(inp.beat, cappedTarget)),
      type: inp.type,
    }));
    const activeLoopCapture = this.loopCapturedInputs[this.loopCapturedInputs.length - 1];
    for (const inp of clampedInputs) {
      activeLoopCapture.push({ beat: inp.beat, type: inp.type });
    }
    this.comboBeforeEvent = this.state.combo;
    const out = step({
      config: this.engineConfig,
      state: this.state,
      fromBeat: this.lastBeat,
      toBeat: cappedTarget,
      inputs: clampedInputs,
    });
    // `Math.max` rather than blind assignment so a regressing audio
    // clock (Web Audio glitches under heavy CPU, Safari resume bugs)
    // can't push `this.lastBeat` backward. Captured replay inputs are
    // clamped to `[lastBeat, cappedTarget]`, and the server's replay
    // validator now enforces monotonic input beats — a backward step
    // would silently invalidate the run.
    this.lastBeat = Math.max(this.lastBeat, cappedTarget);
    for (const ev of out.events) {
      this.handleEvent(ev);
      this.allEvents.push(ev);
      this.opts.onEvent?.(ev);
      this.comboBeforeEvent = this.state.combo;
    }

    // Detect mid-run star/cup milestones (1/3, 2/3 of loop 1; halfway
    // through loop 2). The engine's `completed` event handles the
    // end-of-loop ones (star_3, cup_2, cup_3) below.
    this.detectAndFireMidRunMilestones();

    // Give the player a beat to read the "Miss" floating text from a
    // normal miss followed by the results overlay sliding in.
    this.drawFrame(this.state.beat, null);

    if (!this.state.alive && this.status !== 'failed') {
      this.finishSession('failed');
      return;
    }
    if (this.state.completed) {
      this.handleLoopComplete();
      return;
    }

    this.rafId = requestAnimationFrame(this.tick);
  };

  /**
   * Look for any star/cup milestones the engine just crossed and
   * dispatch FX + the {@link GameSessionOptions.onMilestone} callback.
   * Mid-run milestones don't pause the run; the loop-1 finale (star_3)
   * is handled by {@link handleLoopComplete} since the engine signals
   * it via the `completed` event, not a beat threshold.
   */
  private detectAndFireMidRunMilestones(): void {
    if (!this.enableLoops) return;
    const candidates = this.loopState.detectMidRunMilestones(this.state.beat);
    for (const kind of candidates) {
      if (this.loopState.markFired(kind)) {
        this.spawnMilestoneFx(kind);
        this.opts.onMilestone?.({
          kind,
          loopIndex: this.loopState.getLoopIndex(),
          starsEarned: this.loopState.countStars(),
          cupsEarned: this.loopState.countCups(),
          pause: false,
        });
      }
    }
  }

  /**
   * Engine fired `completed` for the active loop. With loops enabled
   * the run is infinite — every completion advances into the next
   * repeat at the next speed step, and only player death ends the
   * run. The very first transition (loop 1 → loop 2) pauses for the
   * "INFINITE MODE" overlay; every subsequent transition is silent.
   * With loops disabled (tutorial / replay), completion ends the run
   * normally.
   */
  private handleLoopComplete(): void {
    const completionMilestone = this.enableLoops
      ? this.loopState.detectCompletionMilestone()
      : null;
    if (completionMilestone && this.loopState.markFired(completionMilestone)) {
      this.spawnMilestoneFx(completionMilestone);
      // The loop-1 finale is the only completion that pauses gameplay
      // for an overlay; the others fire as flair on the way to the
      // next loop.
      const isLoopOneFinale = this.loopState.getLoopIndex() === 0;
      this.opts.onMilestone?.({
        kind: completionMilestone,
        loopIndex: this.loopState.getLoopIndex(),
        starsEarned: this.loopState.countStars(),
        cupsEarned: this.loopState.countCups(),
        pause: isLoopOneFinale,
      });
    }
    if (this.enableLoops) {
      // Cancel the pending RAF in both transition branches: the
      // loop-1 finale waits for the PlayPage overlay before resuming,
      // and the silent hops have startNextLoop() reschedule their own
      // RAF. Letting the old one fire would race against the new tick
      // and double-step engine state.
      this.setStatus('awaiting_next_loop');
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      const isLoopOneFinale = this.loopState.getLoopIndex() === 0;
      if (isLoopOneFinale) {
        // Hand control to the PlayPage overlay. The audio is allowed
        // to keep playing the song's tail under the overlay; the
        // PlayPage will call startNextLoop() when ready, which stops
        // the music and schedules a fresh source for the new tempo.
        return;
      }
      // Silent loop transition: no UI pause, just keep the engine
      // ticking but at the next playback rate. startNextLoop() handles
      // the audio restart cleanly.
      void this.startNextLoop();
      return;
    }
    if (this.status !== 'completed') {
      this.finishSession('completed');
    }
  }

  private computeLeadInBeat(): number | null {
    const songTime = this.audio.getSongTime();
    if (!isFinite(songTime)) return null;
    if (songTime >= 0) return null;
    const beatsRemaining = -songTime * (this.chart.bpm / 60);
    return Math.max(0, beatsRemaining);
  }

  private tickBeatPulse(currentBeat: number, dt: number): void {
    const beatNumber = Math.floor(currentBeat);
    if (beatNumber !== this.lastBeatPulseBeat && currentBeat >= 0) {
      this.lastBeatPulseBeat = beatNumber;
      this.currentBeatPulse = 1;
    } else {
      const beatsPerSec = this.chart.bpm / 60;
      const decayPerSec = beatsPerSec * 1.6;
      this.currentBeatPulse = Math.max(0, this.currentBeatPulse - decayPerSec * dt);
    }
    if (this.shake.amount > 0) {
      this.shake.amount = Math.max(0, this.shake.amount - this.shake.decay * dt);
    }
    if (this.flipAnim > 0) {
      this.flipAnim = Math.max(0, this.flipAnim - 6 * dt);
    }
    this.tickDropFlash(currentBeat);
  }

  /**
   * Sells the EDM moment by flashing the screen white for ~80ms whenever
   * the playhead crosses the start of a 'drop' section. Sections that have
   * already fired are recorded in `firedDropSections` so a single drop
   * doesn't keep flashing while we're inside it.
   */
  private tickDropFlash(currentBeat: number): void {
    for (let i = 0; i < this.chart.sections.length; i++) {
      const sec = this.chart.sections[i];
      if (sec.kind !== 'drop') continue;
      if (this.firedDropSections.has(i)) continue;
      // Fire as soon as the playhead has crossed startBeat, no upper bound.
      // Pause+resume or a slow frame can otherwise jump past a narrow window
      // and silently skip the flash; once fired we never re-fire for this
      // section (keyed by index, not startBeat, so two drops at the same
      // beat each get their own flash).
      if (currentBeat >= sec.startBeat) {
        this.firedDropSections.add(i);
        this.flashes.push({ type: 'drop', t: 0.18, intensity: 1 });
        this.addShake(6);
      }
    }
  }

  /**
   * Seed `cachedRect` from the canvas's current bounds and observe
   * size changes via `ResizeObserver`. Replaces the per-frame
   * `getBoundingClientRect()` call — on mobile that can synchronously
   * trigger a layout pass once anything else on the page invalidates
   * style, and the per-frame cost shows up as dropped frames on dense
   * charts.
   */
  private attachResizeObserver(): void {
    const canvas = this.opts.canvas;
    const rect = canvas.getBoundingClientRect();
    this.cachedRect.width = rect.width;
    this.cachedRect.height = rect.height;
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      this.cachedRect.width = entry.contentRect.width;
      this.cachedRect.height = entry.contentRect.height;
    });
    this.resizeObserver.observe(canvas);
  }

  private drawFrame(beat: number, leadInBeat: number | null): void {
    const rect = this.cachedRect;
    // Show the TAP hint only for first-time players, and only until they hit
    // their first required pad (perfects + goods > 0).
    const stillNeedsHint =
      !!this.opts.firstTime &&
      this.state.perfects + this.state.goods === 0 &&
      this.state.alive &&
      !this.state.completed;
    this.renderer.draw({
      chart: this.chart,
      state: this.state,
      // Don't clamp at 0: during lead-in we deliberately pass a negative
      // beat so the camera shows track upcoming from beat 0 onward.
      beat,
      width: rect.width,
      height: rect.height,
      particleEmissions: this.particleEmissions,
      flashes: this.flashes,
      floatingTexts: this.floatingTexts,
      shake: this.shake,
      beatPulse: this.currentBeatPulse,
      flipAnim: this.flipAnim,
      countdown: leadInBeat,
      paused: this.status === 'paused',
      showTapHint: stillNeedsHint,
    });
  }

  private toCssPx(xPlayfield: number, yPlayfield: number): { x: number; y: number } {
    // Use the renderer's chosen DPR (capped via the render-quality tier
    // — 2 on desktop, 1.5 on phones) rather than `window.devicePixelRatio`,
    // so popup positions are correct on >2× devices.
    const dpr = this.renderer.getRenderDpr();
    const x = xPlayfield * (this.opts.canvas.width / PLAYFIELD_WIDTH / dpr);
    const y = yPlayfield * (this.opts.canvas.height / PLAYFIELD_HEIGHT / dpr);
    return { x, y };
  }

  private spawnFloatingText(
    text: string,
    x: number,
    y: number,
    colour: string,
    opts: { big?: boolean; vy?: number; scale?: number } = {},
  ): void {
    const big = opts.big ?? false;
    const life = big ? 1.0 : 0.7;
    // Slight horizontal jitter so rapid back-to-back popups don't
    // perfectly stack on top of each other.
    const jitterX = big ? 0 : (Math.random() - 0.5) * 28;
    this.floatingTexts.push({
      text,
      x: x + jitterX,
      y: y - 28,
      colour,
      vy: opts.vy ?? -50,
      scale: opts.scale ?? 1,
      life,
      maxLife: life,
      big,
    });
  }

  private addShake(amount: number): void {
    this.shake.amount = Math.max(this.shake.amount, amount);
    // Always tie decay to the most-recent shake amount so a small post-death
    // perfect tap doesn't decay at the death's huge decay rate.
    this.shake.decay = amount * 6;
  }

  /**
   * Star/cup milestone "yes!" moment. A bright flash, a colourful
   * particle burst at the player, a big floating icon (★ or 🏆),
   * a small shake, and a tier-aware fanfare. Designed to read in
   * ~1s so it doesn't make the player miss the next pad.
   *
   * The loop-1 finale (star_3) gets the same in-engine FX *plus* the
   * PlayPage overlay; the overlay's pause is what gives the player
   * time to read it, the in-engine FX just sells the moment as the
   * music ducks under it.
   */
  private spawnMilestoneFx(kind: MilestoneKind): void {
    const isStar = milestoneIsStar(kind);
    const tier = milestoneTier(kind);
    const colour = isStar ? '#ffd84d' : '#ffb13b';
    const surfaceY = this.state.currentSurface === 'floor' ? FLOOR_Y : CEILING_Y;
    const { x, y } = this.toCssPx(PLAYER_X, surfaceY);
    const { x: cx, y: cy } = this.toCssPx(PLAYER_X, (FLOOR_Y + CEILING_Y) / 2);

    this.particleEmissions.push({ type: 'milestone', x, y, colour });
    this.flashes.push({
      type: 'milestone',
      t: tier === 3 ? 0.6 : 0.4,
      intensity: 0.8 + tier * 0.2,
      colour,
    });
    const glyph = isStar ? '\u2605' : '\u{1F3C6}';
    this.spawnFloatingText(`${glyph} x${tier}`, cx, cy, colour, {
      big: true,
      vy: -90,
      scale: tier === 3 ? 1.4 : 1.15,
    });
    this.addShake(tier === 3 ? 14 : 6 + tier * 2);
    this.audio.playMilestoneFanfare(tier);
    if (kind === 'star_3') {
      // Duck the song under the loop-1 finale overlay; the overlay's
      // 3-2-1 countdown into loop 2 then snaps the music to its new
      // tempo via restartForLoop().
      this.audio.duckMusic(0.12, 350);
      this.audio.playVictorySting();
    }
  }

  private handleEvent(ev: EngineEvent): void {
    if (ev.type === 'pad_resolved') {
      const res = ev.payload as { verdict: 'perfect' | 'good' | 'miss'; padId: string };
      const pad = this.chart.pads.find((p) => p.id === res.padId);
      const padSurfaceY = pad
        ? pad.surface === 'floor'
          ? FLOOR_Y
          : CEILING_Y
        : this.state.currentSurface === 'floor'
          ? FLOOR_Y
          : CEILING_Y;
      const { x, y } = this.toCssPx(PLAYER_X, padSurfaceY);
      this.particleEmissions.push({
        type:
          res.verdict === 'perfect'
            ? 'hit_perfect'
            : res.verdict === 'good'
              ? 'hit_good'
              : 'hit_miss',
        x,
        y,
      });

      const previousCombo = this.comboBeforeEvent;
      const isComboBreak = res.verdict === 'miss' && previousCombo >= 5;
      const isBigComboBreak = res.verdict === 'miss' && previousCombo >= 25;

      this.flashes.push({
        type: res.verdict,
        t: isBigComboBreak ? 0.45 : res.verdict === 'perfect' ? 0.3 : 0.22,
        intensity: isBigComboBreak ? 1.4 : res.verdict === 'perfect' ? 1.2 : 1,
      });

      const label =
        res.verdict === 'perfect' ? 'PERFECT!' : res.verdict === 'good' ? 'Good' : 'Miss';
      const colour =
        res.verdict === 'perfect'
          ? '#3bff9d'
          : res.verdict === 'good'
            ? '#16e0ff'
            : '#ff3b5c';
      const playerCenterY = (FLOOR_Y + CEILING_Y) / 2;
      const { x: textX, y: textY } = this.toCssPx(PLAYER_X, playerCenterY);
      const comboScale = res.verdict === 'perfect' && this.state.combo >= 50 ? 1.4 : 1;
      this.spawnFloatingText(label, textX, textY, colour, { scale: comboScale });

      if (res.verdict === 'miss') {
        this.addShake(isBigComboBreak ? 12 : 4);
        this.comboMilestonesHit.clear();
        if (isComboBreak) {
          this.audio.scheduleComboBreak();
        }
      } else if (res.verdict === 'perfect') {
        this.addShake(4);
      }

      this.audio.scheduleHitFeedback(res.verdict);

      for (const milestone of COMBO_MILESTONES) {
        if (this.state.combo === milestone && !this.comboMilestonesHit.has(milestone)) {
          this.comboMilestonesHit.add(milestone);
          const { x: cx, y: cy } = this.toCssPx(
            PLAYER_X,
            (FLOOR_Y + CEILING_Y) / 2,
          );
          this.spawnFloatingText(`x${milestone} COMBO!`, cx, cy, '#ff2bd6', {
            big: true,
            vy: -80,
          });
          this.audio.scheduleComboMilestone();
          this.addShake(3);
        }
      }
    } else if (ev.type === 'flip') {
      const { x, y } = this.toCssPx(PLAYER_X, (FLOOR_Y + CEILING_Y) / 2);
      this.particleEmissions.push({ type: 'flip', x, y });
      this.flashes.push({ type: 'flip', t: 0.18, intensity: 0.8 });
      this.flipAnim = 1;
      this.audio.scheduleFlip();
    } else if (ev.type === 'burst_tap') {
      const payload = ev.payload as BurstTapPayload;
      const surfaceYPlayfield = this.state.currentSurface === 'floor' ? FLOOR_Y : CEILING_Y;
      const { x, y } = this.toCssPx(PLAYER_X, surfaceYPlayfield);
      this.particleEmissions.push({ type: 'flip', x, y });
      this.audio.scheduleMashTap();
      // Subtle shake every 4 taps so the screen feels like it's reacting
      // without becoming a chaos blender on a fast mash.
      if (payload.tapCount % 4 === 0) this.addShake(2);
    } else if (ev.type === 'burst_complete') {
      const payload = ev.payload as BurstCompletePayload;
      const playerCenterY = (FLOOR_Y + CEILING_Y) / 2;
      const { x, y } = this.toCssPx(PLAYER_X, playerCenterY);
      this.spawnFloatingText(`+${payload.bonus}`, x, y, BURST_CHAMBER_COLOUR, {
        big: true,
        vy: -60,
      });
      this.flashes.push({ type: 'perfect', t: 0.3, intensity: 1 });
      this.addShake(6);
      this.audio.scheduleComboMilestone();
    } else if (ev.type === 'hp_changed') {
      const payload = ev.payload as HpChangedPayload;
      const surfaceYPlayfield = this.state.currentSurface === 'floor' ? FLOOR_Y : CEILING_Y;
      const { x, y } = this.toCssPx(PLAYER_X, surfaceYPlayfield);
      const drained = payload.reason === 'drained';
      if (drained) {
        this.particleEmissions.push({ type: 'death', x, y });
        this.flashes.push({ type: 'death', t: 0.6, intensity: 1.5 });
        this.spawnFloatingText('GAME OVER', x, y, '#ff3b5c', { big: true, vy: -30 });
        this.addShake(14);
        this.audio.playDeath();
      } else if (payload.reason === 'lifeline') {
        // Survived a fatal hit by the lifeline rule - flag this loud and
        // celebratory, not as damage. The player's HP is now at 1, so a
        // standard "-15 HP" floating red text would feel like punishment.
        // A bright "LIFELINE!" call-out plus a heavier shake reads as
        // "you nearly died, but the game caught you", which is the
        // intended emotional beat of the rule.
        const playerCenterY = (FLOOR_Y + CEILING_Y) / 2;
        const { x: cx, y: cy } = this.toCssPx(PLAYER_X, playerCenterY);
        this.spawnFloatingText('LIFELINE!', cx, cy, '#3bd8ff', {
          big: true,
          vy: -50,
        });
        this.flashes.push({ type: 'death', t: 0.45, intensity: 1.0 });
        this.addShake(12);
        this.audio.playDeath();
      } else if (payload.delta < 0) {
        const isHazard = payload.reason === 'hazard';
        const isEmptyTap = payload.reason === 'empty_tap';
        const showEmptyTapToast =
          isEmptyTap && this.emptyTapToastsShown < this.maxTutorialHints;
        this.flashes.push({
          type: 'death',
          t: isHazard ? 0.35 : 0.2,
          intensity: isHazard ? 0.9 : 0.5,
        });
        if (showEmptyTapToast) {
          this.emptyTapToastsShown += 1;
          // Amber teaching toast replaces the standard "-4 HP" red text so
          // the player learns the rule (tap *on* the pad, not when nothing
          // is in the window) instead of just registering the damage. Says
          // "WAIT FOR THE PAD!" rather than "NO PAD HERE!" because the
          // penalty actually fires when a pad is visible-but-not-yet at the
          // receptor (the dead-air case is silently waived).
          const playerCenterY = (FLOOR_Y + CEILING_Y) / 2;
          const { x: cx, y: cy } = this.toCssPx(PLAYER_X, playerCenterY);
          this.spawnFloatingText('WAIT FOR THE PAD!', cx, cy, '#ffae3b', {
            big: true,
            vy: -60,
          });
        } else {
          this.spawnFloatingText(`${payload.delta} HP`, x, y, '#ff3b5c', { vy: -30 });
        }
        this.addShake(isHazard ? 9 : 4);
        if (isHazard) {
          this.audio.playDeath();
        }
      }
    }
  }

  private finishSession(status: SessionStatus): void {
    this.setStatus(status);
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    const loopsCleared = this.getLoopsCleared();
    this.outcome = {
      // Run is "completed" once at least one full loop is cleared,
      // mirroring the server's `loopsCompleted > 0` rule. A player
      // who dies on loop 5 still counts as having completed the
      // chart for leaderboard / progression purposes.
      completed: this.state.completed || loopsCleared >= 1,
      score: this.state.score,
      combo: this.state.combo,
      maxCombo: this.state.maxCombo,
      perfects: this.state.perfects,
      goods: this.state.goods,
      misses: this.state.misses,
      accuracy: computeAccuracy(this.state.perfects, this.state.goods, 0, this.state.misses),
      deathBeat: this.state.deathBeat,
      deathReason: this.state.deathReason ?? undefined,
    };
    // Cut the song the moment the run ends. A clear can land seconds before
    // the song's natural end, and a death almost always happens mid-track;
    // either way we don't want the music droning on under the results
    // overlay. The fade is short so the sting (which goes through the
    // separate SFX bus) still reads as the dominant audio cue.
    this.audio.stopMusic();
    // End-of-run sting: victory for a clear, failure for a true loop-1
    // death. Multi-loop deaths after surviving at least one full loop
    // deliberately get no sting — the killing-blow death sound already
    // played, and a sad chord on top would clash with the "you
    // survived 5 loops, nice run" framing of the results overlay.
    if (this.state.completed && this.state.score > 0) {
      this.audio.playVictorySting();
    } else if (!this.state.completed && loopsCleared === 0) {
      this.audio.playFailureSting();
    }
    this.opts.onComplete?.(this.outcome);
  }

  private buildTestHook(): GameSessionTestHook {
    return {
      getOutcome: () => this.outcome,
      getStatus: () => this.status,
      getState: () => this.state,
      getBeat: () => this.audio.getCurrentBeat(),
      getEvents: () => this.allEvents.slice(),
      isAudioStarted: () => this.audio.isStarted,
      getAudioScheduleErrorCount: () => this.audio.debugScheduleErrorCount,
      getActiveSfxVoices: () => this.audio.debugActiveSfxVoices,
    };
  }
}

export interface GameSessionTestHook {
  getOutcome: () => ReplayOutcome | null;
  getStatus: () => SessionStatus;
  getState: () => EngineState;
  getBeat: () => number;
  getEvents: () => EngineEvent[];
  isAudioStarted: () => boolean;
  /** Number of times an item.schedule() callback threw. Tests assert 0. */
  getAudioScheduleErrorCount: () => number;
  /** How many SFX voices are currently allocated. Tests assert <= cap. */
  getActiveSfxVoices: () => number;
}

export function buildPerfectReplayInputs(chart: PulseChart): EngineInput[] {
  return chart.pads
    .filter((p: PulseChart['pads'][number]) => !p.optional)
    .map((p: PulseChart['pads'][number]) => ({ beat: p.beat, type: 'flipDown' as const }))
    .sort((a: { beat: number }, b: { beat: number }) => a.beat - b.beat);
}
